import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useInterval} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {useEffect, useRef, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES} from '../../../constants/typography';
import {Text} from '../../atoms/Text';

export interface RotatingTextProps {
  className?: string;
  color?: keyof typeof COLORS;
  interval?: number;
  transitionDuration?: number;
  labels: string[];
  size?: keyof typeof FONT_SIZES;
}

export const RotatingText: React.FC<RotatingTextProps> = React.memo(
  function RotatingText({
    className,
    color = 'Black',
    interval = 2000,
    transitionDuration = 0.3,
    labels,
    size = 'px14',
  }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [labelHeights, setLabelHeights] = useState<number[]>([]);
    const [isResetting, setIsResetting] = useState(false);
    const labelRefs = useRef<(HTMLElement | null)[]>([]);
    const hydrationDone = useHydrationDone();

    useEffect(() => {
      const heights = labelRefs.current.map(ref => ref?.offsetHeight || 0);
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setLabelHeights(heights);
    }, [labels]);

    useInterval(() => {
      setCurrentIndex(prev => prev + 1);
    }, interval);

    const maxHeight = Math.max(...labelHeights, 0);
    const hasCalculatedHeights = labelHeights.length > 0;

    // Reset position seamlessly after completing one full cycle
    useEffect(() => {
      if (currentIndex >= labels.length) {
        const transitionTimer = setTimeout(() => {
          // eslint-disable-next-line local-rules/set-state-in-use-effect
          setIsResetting(true);
          setCurrentIndex(0);
          const resetTimer = setTimeout(() => {
            // eslint-disable-next-line local-rules/set-state-in-use-effect
            setIsResetting(false);
          }, 50);
          return () => clearTimeout(resetTimer);
        }, transitionDuration * 1000);

        return () => clearTimeout(transitionTimer);
      }
    }, [currentIndex, labels.length, transitionDuration]);

    // Translate based on max height to keep labels in fixed slots
    const translateY = maxHeight * currentIndex;

    // On SSR, only render the first label to avoid showing all labels
    const labelsToRender = hydrationDone ? [...labels, labels[0]] : [labels[0]];

    return (
      <Wrapper
        $height={hasCalculatedHeights ? maxHeight : undefined}
        className={className}
      >
        <Labels
          $translateY={translateY}
          $transitionDuration={isResetting ? 0 : transitionDuration}
        >
          {labelsToRender.map((label, index) => (
            <LabelSlot
              $height={hasCalculatedHeights ? maxHeight : undefined}
              key={hydrationDone ? index : 0}
            >
              <StyledText
                ref={el => {
                  labelRefs.current[index] = el;
                }}
                size={size}
                color={color}
              >
                {label}
              </StyledText>
            </LabelSlot>
          ))}
        </Labels>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div<{$height?: number}>`
  width: 100%;
  text-align: center;
  height: ${props =>
    props.$height !== undefined ? `${props.$height}px` : 'auto'};
  overflow: hidden;
`;

const Labels = styled.div<{$translateY: number; $transitionDuration: number}>`
  display: flex;
  flex-direction: column;
  transition: transform ${props => props.$transitionDuration}s ease;
  transform: ${props => `translateY(-${props.$translateY}px)`};
`;

const LabelSlot = styled.div<{$height?: number}>`
  height: ${props =>
    props.$height !== undefined ? `${props.$height}px` : 'auto'};
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const StyledText = styled(Text)`
  display: block;
`;
